import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetClient } from "@/hooks/clients";
import { useDeleteReservation, useGetReservations } from "@/hooks/reservations";
import { useGetTourAgency } from "@/hooks/tour-agencies";
import { useGetWorker } from "@/hooks/workers";
import { T } from "@/i18n";
import { cn, dateToString } from "@/lib/utils";
import { Reservation, ReservationState } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, EuroIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const stateToColors: Record<ReservationState, string> = {
  [ReservationState.Pending]:
    "bg-white hover:bg-white/80 text-black border-foreground",
  [ReservationState.Confirmed]:
    "bg-yellow-400 hover:bg-yellow-400/80 text-black",
  [ReservationState.NoShow]:
    "bg-primary hover:bg-primary/80 text-primary-foreground",
  [ReservationState.OnGoing]: "bg-orange-400 hover:bg-orange-400/80 text-black",
  [ReservationState.Canceled]: "bg-gray-400 hover:bg-gray-400/80 text-black",
  [ReservationState.Completed]: "bg-green-400 hover:bg-green-400/80 text-black",
  [ReservationState.Refunded]: "bg-teal-400 hover:bg-teal-400/80 text-black",
} as const;

const stateToBorders: Record<ReservationState, string> = {
  [ReservationState.Pending]: "border-foreground",
  [ReservationState.Confirmed]: "border-yellow-400",
  [ReservationState.NoShow]: "border-primary",
  [ReservationState.OnGoing]: "border-orange-400",
  [ReservationState.Canceled]: "border-gray-400",
  [ReservationState.Completed]: "border-green-400",
  [ReservationState.Refunded]: "border-teal-400",
} as const;

export const Route = createFileRoute("/(reservations)/reservations/")({
  component: ReservationsPage,
});

export function ReservationsPage({ children }: { children?: React.ReactNode }) {
  const query = useGetReservations();

  return (
    <>
      <DomainList
        domain="reservations"
        breadcrumbs={[
          {
            content: (
              <>
                <T tKey="reservations.title" />{" "}
                <BookOpen width={16} height={16} />
              </>
            ),
            href: "/reservations",
          },
        ]}
        tableOptions={{
          hasActions: true,
          columns: [
            {
              header: "reservations.fields.tourAgencyId",
            },
            {
              header: "reservations.fields.agency",
            },
            {
              header: "reservations.fields.groupLeaderId",
            },
            {
              header: "reservations.fields.tourStandart",
            },
            {
              header: "reservations.fields.begin",
            },
            {
              header: "reservations.fields.end",
            },
            {
              header: "tours.fields.category",
            },
            {
              header: "tours.fields.region",
            },
            {
              header: "reservations.fields.workerId",
            },
          ],
        }}
        type="table"
        noOutlet
        data={query.data}
        isLoading={query.isLoading}
        newLink="/reservations/new"
        renderItem={(reservation, index, isTable) => (
          <ReservationCard
            reservation={reservation}
            key={index}
            isTable={isTable}
          />
        )}
      />
      {children}
    </>
  );
}
function ReservationCard({
  reservation,
  isTable,
}: {
  reservation: Reservation;
  isTable: boolean;
}) {
  const { t } = useTranslation();
  const { data: tourAgency, isLoading: tourAgencyLoading } = useGetTourAgency(
    "agency",
    reservation.tourAgencyId?.toString() ?? "",
  );
  const { data: groupLeader, isLoading: groupLeaderLoading } = useGetClient(
    reservation.groupLeaderId?.toString() ?? "",
  );
  const { data: worker, isLoading: workerLoading } = useGetWorker(
    reservation.workerId?.toString() ?? "-",
    { enabled: typeof reservation.workerId === "number" },
  );
  const { mutateAsync } = useDeleteReservation();
  const deleteReservation = () => mutateAsync(reservation.id.toString());
  return (
    <DomainListItemCard
      table={isTable}
      rowClassName={cn(
        "relative",
        stateToColors[reservation.state ?? ReservationState.Pending],
      )}
      className={cn(
        "relative",
        stateToBorders[reservation.state ?? ReservationState.Pending],
      )}
      domain="reservations"
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      title={
        tourAgencyLoading ? (
          <Skeleton className="h-7 w-2/3" />
        ) : (
          (tourAgency?.tour?.name ?? "-")
        )
      }
      description={
        tourAgencyLoading ? (
          <Skeleton className="h-5 w-2/3" />
        ) : (
          (tourAgency?.agency?.name ?? "-")
        )
      }
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      viewLink={(children, key) => (
        <Link
          key={key}
          to="/reservations/$id"
          params={{ id: reservation.id.toString() }}
        >
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/reservations/$id/edit"
          params={{ id: reservation.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          icon: () => <EuroIcon />,
          label: "reservations.fulfillments.title",
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/reservations/$id/fulfillments"
              params={{ id: reservation.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        {
          label: t("reservations.fields.groupLeaderId"),
          value: groupLeaderLoading ? (
            <Skeleton className="inline-block h-2 w-2/3" />
          ) : (
            groupLeader?.name
          ),
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("reservations.fields.tourStandart"),
          tableClassName: "whitespace-nowrap",
          value: reservation.tourStandart ? (
            <T tKey={`reservations.tourStandard.${reservation.tourStandart}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("reservations.fields.begin"),
          value: dateToString(reservation.begin),
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("reservations.fields.end"),
          value: dateToString(reservation.end),
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("tours.fields.category"),
          value: tourAgencyLoading ? (
            <Skeleton className="h-7 w-2/3" />
          ) : tourAgency?.tour?.category ? (
            <T tKey={`tours.categories.${tourAgency.tour.category}`}></T>
          ) : (
            "-"
          ),
        },
        {
          label: t("tours.fields.region"),
          value: tourAgencyLoading ? (
            <Skeleton className="h-7 w-2/3" />
          ) : (
            (tourAgency?.tour?.regionNavigation?.name ?? "-")
          ),
        },
        {
          label: t("reservations.fields.workerId"),
          value: workerLoading ? (
            <Skeleton className="h-7 w-2/3" />
          ) : (
            (worker?.name ?? "-")
          ),
          tableClassName: "whitespace-nowrap",
        },
      ]}
      onDelete={deleteReservation}
    >
      <Badge
        className={cn(
          "absolute left-4 top-0 -translate-y-1/2 font-bold",
          stateToColors[reservation.state ?? ReservationState.Pending]
            .split(" ")
            .filter((c) => !c.startsWith("hover:"))
            .join(" "),
        )}
      >
        <T
          tKey={`reservations.state.${reservation.state ?? ReservationState.Pending}`}
        />
      </Badge>
    </DomainListItemCard>
  );
}
